'use client';

import { ChevronRight, type LucideIcon } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSidebar } from 'src/components/ui/sidebar';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from 'src/components/ui/sidebar';
import { resetAppDetails } from 'src/redux/appBuilder/appDetails';
import { resetViewAppStepperDetails } from 'src/redux/viewApp/stepperDetails';
import {
  resetAppStepperDetails,
  updateAppActiveStep,
  updateAppType,
} from 'src/redux/appBuilder/stepperDetails';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { AppType } from 'src/redux/appBuilder/stepperDetails/types';
import { getAppType } from 'src/redux/appBuilder/stepperDetails/selectors';

export function NavMain({
  items,
}: {
  items: {
    title: string;
    url: string;
    icon?: LucideIcon;
    isActive?: boolean;
    items?: {
      isActive?: boolean;
      title: string;
      type?: string;
      url: string;
    }[];
  }[];
}) {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const newAppType = useAppSelector(getAppType);
  const { state } = useSidebar();

  const handleClick = (item: { type?: string; url: string; title: string }) => {
    dispatch(resetAppStepperDetails());
    dispatch(resetAppDetails());
    dispatch(resetViewAppStepperDetails());
    if (item.url === '/new-application') {
      if (item.type) {
        dispatch(updateAppType(item.type as AppType));
        dispatch(updateAppActiveStep(1));
      }
    }
    navigate(item.url);
  };

  return (
    <SidebarGroup>
      <SidebarMenu>
        {items.map((item) => (
          <Collapsible
            key={item.title}
            asChild
            defaultOpen={item.isActive}
            className="group/collapsible"
            onOpenChange={(isOpen) => {
              localStorage.setItem(`nav-${item.title}-state`, JSON.stringify(isOpen));
            }}
          >
            <SidebarMenuItem>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton
                  tooltip={item.title}
                  onClick={() => {
                    if (state === 'collapsed' && item.url) {
                      navigate(item.url);
                    }
                  }}
                >
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                  <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarMenuSub>
                  {item.items?.map((subItem) => (
                    <SidebarMenuSubItem key={subItem.title}>
                      <SidebarMenuSubButton
                        onClick={() => handleClick(subItem)}
                        className="cursor-pointer"
                        isActive={
                          subItem.type
                            ? newAppType === subItem.type
                            : subItem.url === pathname
                        }
                        asChild
                      >
                        <span>{subItem.title}</span>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
